import { type QueryClient } from "@tanstack/react-query";
import type { ReactElement } from "react";
import {
  Route,
  redirect,
  type LoaderFunction,
  type LoaderFunctionArgs,
} from "react-router";

import { queryClient as defaultQueryClient } from "@/providers/QueryClient";
import { orgStorage } from "@/shared/auth";
import { FeatureFlagService } from "@/shared/domain/featureFlags";
import type { FeatureFlagName } from "@/shared/domain/featureFlags/FeatureFlagName";
import { featureFlagQueryKeys } from "@/shared/domain/featureFlags/hooks/featureFlagQueryKeys";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";

import { composeLoaders } from "./composeLoaders";

interface MakeLoaderOptions {
  flagName: FeatureFlagName;
  redirectTo: string | ((args: LoaderFunctionArgs) => string);
}

interface FeatureFlagRouteOptions extends MakeLoaderOptions {
  path: string;
  element: ReactElement;
  /**
   * The route's own loader, run alongside the flag gate (see
   * `composeLoaders`): the gate's redirect wins, so with the flag off the
   * element still never mounts.
   */
  loader?: LoaderFunction;
}

/**
 * Factory for a `<Route>` that gates `element` on a feature flag: when
 * the flag is enabled for the current org the gated element renders;
 * otherwise the user is redirected to `redirectTo` before the gated UI
 * mounts.
 *
 * Gating runs in the route loader rather than inside `element` so a
 * flag-off render never mounts the gated tree, even for one frame. A
 * client-side `<Navigate />` inside the gated element would flash the
 * gated UI on a cold load while the flag query was in flight.
 *
 * Uses the app's shared TanStack Query client so the cache entry is
 * shared with `useCheckFeatureFlag`. See `makeFeatureFlagRedirectLoader`
 * for the loader's decision branches and cache TTL.
 */
export function featureFlagRoute({
  path,
  flagName,
  redirectTo,
  element,
  loader,
}: FeatureFlagRouteOptions): ReactElement {
  const gate = makeFeatureFlagRedirectLoader(defaultQueryClient, {
    flagName,
    redirectTo,
  });
  return (
    <Route
      key={path}
      path={path}
      loader={loader ? composeLoaders(gate, loader) : gate}
      element={element}
    />
  );
}

/**
 * Loader factory; exposed separately from `featureFlagRoute` so the
 * branch decisions are testable without a `createBrowserRouter`.
 *
 * Resolves to one of four outcomes when React Router matches the gated
 * route:
 *
 *   1. No current org in local storage — return `null` and let the route
 *      render. The app's authentication layout is what bounces an
 *      unauthenticated or org-less user to sign-in / org selection; this
 *      loader does not duplicate that gating.
 *   2. Flag on for the current org — return `null` so React Router
 *      renders the gated element.
 *   3. Flag off, or the current org is absent from the response because
 *      the caller can't access it — return a `redirect()` `Response` pointing at
 *      `redirectTo` (resolved against the loader args when it's a
 *      function). React Router consumes the `Response` and navigates;
 *      the gated element is never mounted.
 *   4. Fetch failure (network error, expired auth, upstream 5xx) —
 *      return a `redirect()` to `redirectTo`. Equivalent on the wire
 *      to the flag-off branch; the gated element never mounts. This
 *      matches the fail-closed default `useCheckFeatureFlag` already
 *      applies on query failure, so the route gate and any in-page
 *      gates degrade the same way.
 */
export function makeFeatureFlagRedirectLoader(
  queryClient: QueryClient,
  { flagName, redirectTo }: MakeLoaderOptions,
): LoaderFunction {
  return async (args) => {
    const orgRecord = orgStorage.getCurrentOrg();
    if (!orgRecord) {
      return null;
    }

    const target =
      typeof redirectTo === "function" ? redirectTo(args) : redirectTo;

    let flagStates: Record<string, Record<string, boolean>>;
    try {
      flagStates = await queryClient.fetchQuery({
        queryKey: featureFlagQueryKeys.states(),
        queryFn: async () => {
          const httpClient = await makeAuthenticatedHttpClient();
          return new FeatureFlagService(httpClient).checkAllFeatureFlags({
            orgId: orgRecord.org_id,
          });
        },
        staleTime: Infinity,
      });
    } catch {
      return redirect(target);
    }

    if (flagStates[orgRecord.org_id]?.[flagName]) {
      return null;
    }

    return redirect(target);
  };
}
